import React, {
  memo,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react'
import { IntakeContext } from 'context/IntakeContext'

// @mui material components
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'

// Material Icons
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { green, blue } from '@mui/material/colors'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'

// Soft UI Dashboard PRO React components
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
// import Footer from 'components/Footer'
// import DefaultCounterCard from 'components/Cards/CounterCards/DefaultCounterCard'
// import styled from 'styled-components'
import { styled } from '@mui/material/styles'
import { CalculationDebug } from './components/CalculationDebug'

//Gauge Chart
import GaugeChart from 'react-gauge-chart'
// import useMediaQuery from '@mui/material/useMediaQuery'

// react-countup components
import CountUp from 'react-countup'
import SoftButton from 'components/SoftButton'

//Accordion
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { useDashboardCalculations } from './hooks/useDashboardCalculations'
import { UpdatedCalcs } from 'admin/UpdatedCalcs'
import { useSearchParams } from 'react-router-dom'
import { CSAssets } from './components/CSAssets'
import { PropertyAssets } from './components/PropertyAssets'
import { InvestmentAssets } from './components/InvestmentAssets'
import { OtherAssets } from './components/OtherAssets'
import { OtherLiabilities } from './components/OtherLiabilities'
import { MortgageLiabilities } from './components/MortgageLiabilities'
import { CreditCardLiabilities } from './components/CreditCardLiabilities'
import { BalanceSheetModalForm } from './components/BalanceSheetModalForm'
import { UpdateFieldModalForm } from './components/UpdateFieldModalForm'
import { UpdateRetirementFieldModalForm } from './components/UpdateRetirementFieldModalForm'
import { ImportFromPlaid } from './components/ImportFromPlaid'
import { apiClient } from 'utils/api/apiClient'
import { testIds } from './constants'
import { BalanceSheetRow } from './components/BalanceSheet/BalanceSheetRow'

import { NormalizedGrossDialog } from './components/NormalizedGrossDialog'
import { LeaveAndEarnings } from './components/LeaveAndEarnings'
import { callZapier } from 'utils/zapier/callZapier'

const RoundIcon = styled('div')(() => ({
  border: '1px solid #ddd',
  padding: '10px',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  margin: 'auto',
}))

//Fix for Gauge Chart Styling
const gaugeChartStyle = {
  height: '100%',
}
const GaugeChartWrapper = styled('div')(({ theme }) => ({
  height: '11vw',
  [theme.breakpoints.down('xl')]: {
    height: '10vw',
  },
  [theme.breakpoints.down('lg')]: {
    height: '17vw',
  },
  [theme.breakpoints.down('md')]: {
    height: '20vw',
  },
  [theme.breakpoints.down('md')]: {
    height: '36vw',
  },
}))

// if (process.env.NODE_ENV !== 'test') {
//   const surflyNew = (function (s, u, r, f, l, y) {
//     s[f] = s[f] || {
//       init: function () {
//         s[f].q = arguments
//       },
//     }
//     l = u.createElement(r)
//     y = u.getElementsByTagName(r)[0]
//     l.async = 1
//     l.src = 'https://surfly.com/surfly.js'
//     y.parentNode.insertBefore(l, y)
//   })(window, document, 'script', 'Surfly')

//   var settings = {
//     // Surfly session options can be set here, or at the Company/Plan levels.
//     widget_key: '1766d1d6fb23435f84ca7bc9a64a8156',
//   }

//   Surfly.init(settings, function (initResult) {
//     if (initResult.success) {
//       // API calls can now be made!
//       if (!Surfly.isInsideSession) {
//         Surfly.button()
//       }
//     } else {
//       console.log('Surfly was unable to initialize properly.')
//     }
//   })
// }
export const Dashboard = memo(() => {
  const { intakeState, updateUser, updateIntakeStateAtKey } =
    useContext(IntakeContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [id, setId] = useState()
  const [version, setVersion] = useState()
  const [type, setType] = useState()
  const [name, setName] = useState()
  const [plaidName, setPlaidName] = useState()
  const [subType, setSubType] = useState()
  const [sortKey, setSortKey] = useState()
  const [amount, setAmount] = useState()
  const [modalTitle, setModalTitle] = useState()
  const [open, setOpen] = useState(false)
  const [openFieldModal, setOpenFieldModal] = useState(false)
  const [openRetirementFieldModal, setOpenRetirementFieldModal] =
    useState(false)
  const {
    daysTillRetirement,
    yearsTillRetirement,
    remainingDaysTillRetirement,
    plannedRetirementMonth,
    plannedRetirementDay,
    plannedRetirementYear,
    normalizedGrossWhileWorking,
    normalizedGrossInRetirement,
    normalizedGrossGap,
  } = useDashboardCalculations()
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundAssets = items?.filter(({ type }) => type === 'asset') || []
    setAssets(foundAssets)
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    setDeductions(foundDeductions)
  }, [])

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  const { readinessScoreAdjustedMax } = UpdatedCalcs({ assets, liabilities })

  const improveScoreHelp = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/3zmzqpy/',
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          phoneNumber: intakeState.phone,
          email: intakeState.email,
        },
      },
      sendNotification: true,
      notificationMessage:
        'A member of the PARCO team will reach out to you shortly! Expect a call to help improve your retirement readiness.',
    })
  }

  const handleCSClickOpen = () => {
    setOpen(true)
    setSubType('checkingsavings')
    setType('asset')
    setModalTitle('Add a Checking or Savings Account')
  }

  const handleViewAsset = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
  }) => {
    setType('asset')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setModalTitle(`Update ${name}`)
  }

  const handleViewLiability = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
  }) => {
    setType('liability')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setModalTitle(`Update ${name}`)
  }

  const handlePropertyClickOpen = () => {
    setOpen(true)
    setSubType('property')
    setType('asset')
    setModalTitle('Add a Property')
  }

  const handleInvestmentClickOpen = () => {
    setOpen(true)
    setSubType('investment')
    setType('asset')
    setModalTitle('Add An Investment')
  }

  const handleOtherAssetClickOpen = () => {
    setOpen(true)
    setSubType('otherassets')
    setType('asset')
    setModalTitle('Add An Asset')
  }

  const handleOtherLiabilityClickOpen = () => {
    setOpen(true)
    setSubType('otherliabilities')
    setType('liability')
    setModalTitle('Add a Liability')
  }

  const handleMortgageClickOpen = () => {
    setOpen(true)
    setSubType('mortgage')
    setType('liability')
    setModalTitle('Add a Mortgage')
  }

  const handleCCClickOpen = () => {
    setOpen(true)
    setSubType('creditcard')
    setType('liability')
    setModalTitle('Add a Credit Card')
  }

  const handleSubmit = () => {
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    setVersion('')
    setId('')
    setType('')
    setName('')
    setPlaidName('')
    setSubType('')
    setAmount('')
    setSortKey('')
    setModalTitle('')
  }
  const handleFieldModalOpen = () => {
    setOpenFieldModal(true)
  }
  const handleFieldModalClose = () => {
    setOpenFieldModal(false)
    updateUser()
  }
  const handleRetirementFieldModalOpen = () => {
    setOpenRetirementFieldModal(true)
  }
  const handleRetirementFieldModalClose = () => {
    setOpenRetirementFieldModal(false)
    updateUser()
  }

  //Currency Formatting
  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (intakeState.tspBalance ? intakeState.tspBalance : 0)

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const netWorth = assetSum - liabilitySum

  return (
    <DashboardLayout>
      <BalanceSheetModalForm
        handleClose={handleClose}
        type={type}
        name={name}
        amount={amount}
        version={version}
        id={id}
        subType={subType}
        setVersion={setVersion}
        setId={setId}
        setType={setType}
        setSubType={setSubType}
        setName={setName}
        plaidName={plaidName}
        setAmount={setAmount}
        typeSubTypeUuid={sortKey}
        open={open}
        handleSubmit={handleSubmit}
        reloadAccounts={getAccounts}
        modalHeader={modalTitle}
      />
      <UpdateFieldModalForm
        handleClose={handleFieldModalClose}
        open={openFieldModal}
        handleSubmit={handleFieldModalClose}
      />
      <UpdateRetirementFieldModalForm
        handleClose={handleRetirementFieldModalClose}
        open={openRetirementFieldModal}
        handleSubmit={handleRetirementFieldModalClose}
      />
      <DashboardNavbar />
      <SoftBox mt={0} p={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5} xl={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <SoftBox px={3} py={2}>
                      <SoftTypography
                        textAlign="center"
                        variant="h5"
                        component="div"
                        color="dark"
                        fontWeight="bold"
                      >
                        Balance Sheet
                        {searchParams.get('plaidSandbox') && (
                          <ImportFromPlaid handleUpdateAccounts={getAccounts} />
                        )}
                      </SoftTypography>
                      <Divider variant="middle" />
                      <SoftTypography
                        textAlign="center"
                        variant="h5"
                        component="div"
                        color="dark"
                        fontWeight="bold"
                      >
                        Assets
                      </SoftTypography>
                      <SoftBox>
                        <BalanceSheetRow
                          accounts={assets}
                          handleAddAccount={handleCSClickOpen}
                          category={CSAssets.targetCategory}
                          handleViewAccount={handleViewAsset}
                          accountType="Checking & Savings"
                          addAccountButtonText="Add a Checking or Savings Account"
                        />
                        <SoftBox
                          sx={{
                            borderRadius: '6px',
                          }}
                        >
                          <Accordion sx={{ boxShadow: 'none' }}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1a-content"
                              id="panel1a-header"
                              sx={{
                                border: 'none',
                                borderBottom: '1px solid #ddd',
                              }}
                            >
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                                mr={1}
                              >
                                <SoftTypography
                                  textAlign="left"
                                  color="inherit"
                                  variant="body2"
                                  fontWeight="bold"
                                >
                                  Thrift Savings Plan
                                </SoftTypography>
                                <SoftTypography
                                  textAlign="right"
                                  color="inherit"
                                  variant="body2"
                                  fontWeight="bold"
                                >
                                  {dollarFormat.format(intakeState.tspBalance)}
                                </SoftTypography>
                              </SoftBox>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                padding: '0px',
                                paddingBottom: '14px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#fafafb',
                              }}
                            >
                              <SoftBox borderBottom="1px solid #ddd">
                                <SoftBox
                                  px={2}
                                  py={2}
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <SoftTypography
                                    textAlign="left"
                                    color="inherit"
                                    variant="body2"
                                  >
                                    Thrift Savings Plan
                                  </SoftTypography>
                                  <SoftBox display="flex">
                                    <SoftTypography
                                      textAlign="right"
                                      color="inherit"
                                      variant="body2"
                                    >
                                      {dollarFormat.format(
                                        intakeState.tspBalance
                                      )}
                                    </SoftTypography>
                                    <IconButton
                                      aria-label="edit"
                                      size="small"
                                      onClick={handleFieldModalOpen}
                                      sx={{
                                        marginTop: '-2px',
                                        marginLeft: '5px',
                                      }}
                                    >
                                      <EditIcon fontSize="inherit" />
                                    </IconButton>
                                  </SoftBox>
                                </SoftBox>
                              </SoftBox>
                              {/* Possibly have them re-enter the flow if they haven't filled out the other TSP Details

                              <SoftButton
                                variant="text"
                                color="dark"
                                sx={{
                                  height: '100%',
                                  display: 'block',
                                  margin: 'auto',
                                  paddingTop: '22px',
                                }}
                                href="onboarding-full?onboardingStep=tspBalance"
                              >
                                {intakeState.tspBalance
                                  ? 'Update Your TSP Balance'
                                  : 'Add Your TSP Balance'}
                              </SoftButton> */}
                              <SoftButton
                                variant="text"
                                color="dark"
                                sx={{
                                  height: '100%',
                                  display: 'block',
                                  margin: 'auto',
                                  paddingTop: '22px',
                                }}
                                onClick={handleFieldModalOpen}
                              >
                                {intakeState.tspBalance
                                  ? 'Update Your TSP Balance'
                                  : 'Add Your TSP Balance'}
                              </SoftButton>
                            </AccordionDetails>
                          </Accordion>
                        </SoftBox>
                      </SoftBox>
                      <BalanceSheetRow
                        accounts={assets}
                        handleAddAccount={handlePropertyClickOpen}
                        category={PropertyAssets.targetCategory}
                        handleViewAccount={handleViewAsset}
                        accountType="Property"
                        addAccountButtonText="Add a Property"
                      />
                      <BalanceSheetRow
                        accounts={assets}
                        handleAddAccount={handleInvestmentClickOpen}
                        category={InvestmentAssets.targetCategory}
                        handleViewAccount={handleViewAsset}
                        accountType="Investments"
                        addAccountButtonText="Add An Investment"
                      />
                      <BalanceSheetRow
                        accounts={assets}
                        handleAddAccount={handleOtherAssetClickOpen}
                        category={OtherAssets.targetCategory}
                        handleViewAccount={handleViewAsset}
                        accountType="Other Assets"
                        addAccountButtonText="Add An Asset"
                      />

                      <SoftBox>
                        <SoftBox
                          mt={2}
                          borderRadius="8px"
                          sx={{
                            backgroundColor: '#fafafb',
                            boxShadow:
                              '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                          }}
                        >
                          <SoftBox
                            px={3}
                            py={2}
                            display="flex"
                            justifyContent="space-between"
                            sx={{ color: green[500] }}
                          >
                            <SoftTypography
                              textAlign="left"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              Total Assets
                            </SoftTypography>
                            <SoftTypography
                              textAlign="right"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              {dollarFormat.format(assetSum)}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                      <SoftTypography
                        textAlign="center"
                        variant="h5"
                        component="div"
                        color="dark"
                        fontWeight="bold"
                        mt={3}
                      >
                        Liabilities
                      </SoftTypography>
                      <BalanceSheetRow
                        accounts={liabilities}
                        handleAddAccount={handleMortgageClickOpen}
                        category={MortgageLiabilities.targetCategory}
                        handleViewAccount={handleViewLiability}
                        accountType="Mortgage"
                        addAccountButtonText="Add a Mortgage"
                      />
                      <BalanceSheetRow
                        accounts={liabilities}
                        handleAddAccount={handleCCClickOpen}
                        category={CreditCardLiabilities.targetCategory}
                        handleViewAccount={handleViewLiability}
                        accountType="Credit Cards"
                        addAccountButtonText="Add a Credit Card"
                      />
                      <BalanceSheetRow
                        accounts={liabilities}
                        handleAddAccount={handleOtherLiabilityClickOpen}
                        category={OtherLiabilities.targetCategory}
                        handleViewAccount={handleViewLiability}
                        accountType="Other Liabilities"
                        addAccountButtonText="Add another Liability"
                      />
                      <SoftBox>
                        <SoftBox
                          mt={2}
                          borderRadius="8px"
                          sx={{
                            backgroundColor: '#fafafb',
                            boxShadow:
                              '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                          }}
                        >
                          <SoftBox
                            px={3}
                            py={2}
                            display="flex"
                            justifyContent="space-between"
                            color="error"
                          >
                            <SoftTypography
                              textAlign="left"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              Total Liabilities
                            </SoftTypography>
                            <SoftTypography
                              textAlign="right"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              {dollarFormat.format(liabilitySum)}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                      <SoftBox>
                        <SoftBox
                          mt={2}
                          borderRadius="8px"
                          sx={{
                            backgroundColor: '#fafafb',
                            boxShadow:
                              '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                          }}
                        >
                          <SoftBox
                            px={3}
                            py={2}
                            display="flex"
                            justifyContent="space-between"
                            sx={{ color: blue[800] }}
                          >
                            <SoftTypography
                              textAlign="left"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              Net Worth
                            </SoftTypography>
                            <SoftTypography
                              textAlign="right"
                              color="inherit"
                              variant="body2"
                              fontWeight="bold"
                            >
                              {dollarFormat.format(netWorth)}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            {/* Change Back when we integrate with Plaid */}
            <Grid item xs={12} lg={7} xl={8}>
              {/* <Grid item xs={12}> */}
              {searchParams.get('normalizedGross') ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <SoftBox p={2}>
                        <Grid container alignItems="center">
                          <Grid item xs={12} sm={3} p={1}>
                            <SoftBox lineHeight={1}>
                              <SoftTypography
                                variant="button"
                                textTransform="capitalize"
                                fontWeight="bold"
                                fontSize="18px"
                                data-testid={testIds.Dashboard.normalizedGross}
                              >
                                Normalized Gross Monthly Income
                              </SoftTypography>
                              <NormalizedGrossDialog />
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} sm={3} p={1}>
                            <SoftBox lineHeight={1}>
                              <SoftTypography
                                variant="button"
                                textTransform="capitalize"
                                fontWeight="bold"
                              >
                                While Working
                              </SoftTypography>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                data-testid={
                                  testIds.Dashboard.grossWhileWorking
                                }
                              >
                                {dollarFormat.format(
                                  normalizedGrossWhileWorking || 0
                                )}
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} sm={3} p={1}>
                            <SoftBox lineHeight={1}>
                              <SoftTypography
                                variant="button"
                                textTransform="capitalize"
                                fontWeight="bold"
                              >
                                In Retirement
                              </SoftTypography>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                data-testid={
                                  testIds.Dashboard.normalizedGrossInRetirement
                                }
                              >
                                {dollarFormat.format(
                                  normalizedGrossInRetirement || 0
                                )}
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={12} sm={3} p={2}>
                            <SoftBox lineHeight={1}>
                              {normalizedGrossGap <= 0 ? (
                                <>
                                  <SoftTypography
                                    variant="button"
                                    textTransform="capitalize"
                                    fontWeight="bold"
                                    display="inline-block"
                                    color="success"
                                  >
                                    Surplus
                                  </SoftTypography>
                                  <SoftTypography
                                    variant="h5"
                                    fontWeight="bold"
                                    color="success"
                                  >
                                    {dollarFormat.format(
                                      Math.abs(normalizedGrossGap)
                                    )}
                                  </SoftTypography>
                                </>
                              ) : (
                                <>
                                  <SoftTypography
                                    variant="button"
                                    textTransform="capitalize"
                                    fontWeight="bold"
                                    display="inline-block"
                                    color="error"
                                  >
                                    Gap
                                  </SoftTypography>
                                  <SoftTypography
                                    variant="h5"
                                    fontWeight="bold"
                                    color="error"
                                  >
                                    {dollarFormat.format(normalizedGrossGap)}
                                  </SoftTypography>
                                </>
                              )}
                            </SoftBox>
                          </Grid>
                        </Grid>
                      </SoftBox>
                      {searchParams.get('leaveAndEarnings') ? (
                        <Accordion
                          sx={[
                            {
                              boxShadow: 'none',
                              marginTop: '0px !important',
                            },
                            {
                              '&:before': { opacity: '1 !important' },
                            },
                          ]}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            sx={{
                              borderBottom: '1px solid #ddd',
                            }}
                          >
                            <SoftBox
                              display="flex"
                              justifyContent="center"
                              width="100%"
                              mr={1}
                            >
                              <SoftTypography
                                textAlign="center"
                                color="inherit"
                                variant="body2"
                                fontWeight="bold"
                              >
                                See How Your Monthly Income Changes in
                                Retirement
                              </SoftTypography>
                            </SoftBox>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              padding: '0px',
                            }}
                          >
                            <LeaveAndEarnings deductions={deductions} />
                          </AccordionDetails>
                        </Accordion>
                      ) : null}
                    </Card>
                  </Grid>
                </Grid>
              ) : null}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    {/* Switch back to mt=3 after we add back in normalized gross */}
                    {/* <SoftBox mt={3}> */}
                    <Card>
                      <SoftBox
                        // bgColor="info"
                        p={2}
                        display="flex"
                        justifyContent="space-between"
                        textAlign="center"
                        alignItems="center"
                        variant="gradient"
                      >
                        <Grid container alignItems="center">
                          <Grid item xs>
                            <SoftTypography
                              textAlign="center"
                              variant="h3"
                              component="div"
                              color="dark"
                            >
                              Retirement Readiness
                            </SoftTypography>
                          </Grid>
                        </Grid>
                      </SoftBox>
                      <SoftBox sx={{ mb: 3, mx: 2 }}>
                        <Grid container alignItems="center">
                          <Grid item xs>
                            <GaugeChartWrapper>
                              <GaugeChart
                                id="gauge-chart2"
                                nrOfLevels={50}
                                cornerRadius={0}
                                percent={readinessScoreAdjustedMax || 0}
                                colors={['#ea0606', '#4caf08']}
                                textColor="#344767"
                                arcWidth={0.25}
                                arcPadding={0}
                                hideText={true}
                                animate={false}
                                style={gaugeChartStyle}
                              />
                            </GaugeChartWrapper>
                            <SoftBox
                              display="inline-block"
                              mx={0}
                              sx={{
                                '@media screen and (min-width: 1400px)': {
                                  marginTop: '3em',
                                },
                              }}
                            >
                              <SoftTypography
                                gutterBottom
                                variant="h1"
                                component="div"
                                mb={0}
                              >
                                <CountUp
                                  end={Math.round(
                                    readinessScoreAdjustedMax * 100
                                  )}
                                  duration={1.5}
                                  separator=","
                                />
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                        </Grid>
                        <Divider variant="middle" />
                        <SoftButton
                          variant="text"
                          color="dark"
                          onClick={improveScoreHelp}
                          sx={{ fontSize: '18px' }}
                        >
                          Improve My Score
                        </SoftButton>
                      </SoftBox>
                    </Card>
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    {/* Switch back to mt=3 after we add back in normalized gross */}
                    {/* <SoftBox mt={3}> */}
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card>
                          <SoftBox p={2}>
                            <Grid container alignItems="center">
                              <Grid item xs={8}>
                                <SoftBox lineHeight={1}>
                                  <SoftTypography
                                    variant="button"
                                    textTransform="capitalize"
                                    fontWeight="bold"
                                  >
                                    TSP Balance
                                  </SoftTypography>
                                  <SoftTypography
                                    variant="h5"
                                    fontWeight="bold"
                                  >
                                    {dollarFormat.format(
                                      intakeState.tspBalance
                                    )}
                                  </SoftTypography>
                                </SoftBox>
                              </Grid>
                              <Grid item xs={4}>
                                <SoftBox>
                                  <RoundIcon>
                                    <AccountBalanceIcon
                                      color="primary"
                                      fontSize="large"
                                      sx={{ color: blue[800] }}
                                    />
                                  </RoundIcon>
                                </SoftBox>
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </Card>
                        {/* <ProgressDoughnutChart
                          icon="paid"
                          title="TSP Balance"
                          count={dollarFormat.format(intakeState.tspBalance)}
                          chart={progressDoughnutChartData}
                        /> */}
                      </Grid>
                      {daysTillRetirement < 0 ? (
                        <Grid item xs={12}>
                          <Card>
                            <SoftBox sx={{ my: 3, mx: 2 }}>
                              <SoftTypography
                                color="dark"
                                variant="body2"
                                fontWeight="bold"
                              >
                                Congratulations on Retiring!
                                <IconButton
                                  aria-label="edit"
                                  size="small"
                                  onClick={handleRetirementFieldModalOpen}
                                  sx={{
                                    marginTop: '-2px',
                                    marginLeft: '5px',
                                  }}
                                >
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </SoftTypography>
                              {/* <Divider variant="middle" /> */}
                              <Grid container alignItems="center">
                                <Grid item xs>
                                  <SoftBox display="inline-block" mx={0}>
                                    <SoftTypography
                                      color="info"
                                      textGradient
                                      component="span"
                                      variant="h4"
                                      fontWeight="bold"
                                    >
                                      {plannedRetirementMonth}{' '}
                                      {plannedRetirementDay},{' '}
                                      {plannedRetirementYear}
                                    </SoftTypography>
                                  </SoftBox>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          </Card>
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Card>
                            <SoftBox sx={{ my: 3, mx: 2 }}>
                              <SoftTypography
                                color="dark"
                                variant="body2"
                                fontWeight="bold"
                              >
                                Expected Retirement Date
                                <IconButton
                                  aria-label="edit"
                                  size="small"
                                  onClick={handleRetirementFieldModalOpen}
                                  sx={{
                                    marginTop: '-2px',
                                    marginLeft: '5px',
                                  }}
                                >
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </SoftTypography>
                              {/* <Divider variant="middle" /> */}
                              <Grid container alignItems="center">
                                <Grid item xs>
                                  <SoftBox display="inline-block" mx={0}>
                                    <SoftTypography
                                      color="info"
                                      textGradient
                                      component="span"
                                      variant="h4"
                                      fontWeight="bold"
                                    >
                                      {plannedRetirementMonth}{' '}
                                      {plannedRetirementDay},{' '}
                                      {plannedRetirementYear}
                                    </SoftTypography>
                                  </SoftBox>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          </Card>
                        </Grid>
                      )}

                      {(daysTillRetirement || 0) <= 0 ? (
                        <></>
                      ) : (
                        <Grid item xs={12}>
                          <Card>
                            <SoftBox p={3} textAlign="center" lineHeight={1.25}>
                              <SoftTypography
                                variant="h1"
                                color="info"
                                fontWeight="bold"
                                textGradient
                              >
                                <SoftBox display="inline-block" mx={0.5}>
                                  <CountUp
                                    end={yearsTillRetirement}
                                    duration={1}
                                  />
                                </SoftBox>
                                <SoftTypography
                                  component="span"
                                  variant="h5"
                                  fontWeight="bold"
                                >
                                  YEARS
                                </SoftTypography>
                                <SoftBox display="inline-block" mx={0.5}>
                                  <CountUp
                                    end={remainingDaysTillRetirement}
                                    duration={1}
                                  />
                                </SoftBox>
                                <SoftTypography
                                  component="span"
                                  variant="h5"
                                  fontWeight="bold"
                                >
                                  DAYS
                                </SoftTypography>
                              </SoftTypography>
                              <SoftTypography
                                variant="h6"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                Until Retirement
                              </SoftTypography>
                            </SoftBox>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                  </SoftBox>
                </Grid>
              </Grid>
              {searchParams.get('improveRetirementScore') ? (
                <Grid
                  container
                  spacing={3}
                  data-testid="retirementScoreImprovement"
                >
                  <Grid item xs={12}>
                    <SoftBox mt={3}>
                      <SoftTypography
                        textAlign="left"
                        variant="h4"
                        component="div"
                        color="dark"
                        mb={1}
                      >
                        Improve Your Retirement Score:
                      </SoftTypography>
                      <Grid container spacing={1}>
                        {/* <MissingOnboardingSteps />
                      <Recommendations
                        recommendations={intakeState.recommendations}
                      /> */}
                        <Grid item xs={12}>
                          <Card>
                            <SoftBox px={3} py={2}>
                              <SoftTypography
                                textAlign="left"
                                variant="h6"
                                component="div"
                                color="dark"
                                fontWeight="bold"
                              >
                                Optimize Your Retirement Plan
                              </SoftTypography>
                              <SoftTypography
                                textAlign="left"
                                color="inherit"
                                variant="body2"
                              >
                                Prioritize Your Long-Term Needs · Let PARCO Help
                                You Evaluate Your Retirement ·{' '}
                                <SoftButton
                                  variant="text"
                                  color="dark"
                                  sx={{
                                    height: '100%',
                                    margin: 'auto',
                                    paddingTop: '5px',
                                    paddingLeft: '5px',
                                    fontSize: '15px',
                                  }}
                                  onClick={improveScoreHelp}
                                >
                                  Talk to the team
                                </SoftButton>
                              </SoftTypography>
                            </SoftBox>
                          </Card>
                        </Grid>
                      </Grid>
                    </SoftBox>
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </SoftBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {/* <RankingsList
              title="transactions"
              date="23 - 30 March 2021"
              rankings={transactionsData}
            /> */}
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <RankingsList
              title="revenue"
              date="01 - 07 June 2021"
              rankings={revenueData}
            /> */}
          </Grid>
        </Grid>
        {process.env.DEBUG && <CalculationDebug />}
      </SoftBox>
      {/* <Footer /> */}
    </DashboardLayout>
  )
})

Dashboard.displayName = 'Dashboard'
